import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trash2, Edit2, BarChart2, Battery, X, Check } from 'lucide-react';
import { useDeviceStore } from '../../stores/deviceStore';
import { DeviceIcon } from '../common/DeviceIcon';
import { useUIStore } from '../../stores/uiStore';
import { ParameterControl } from '../devices/ParameterControl';
import { Parameter } from '../../api/types';

export function DeviceDetailScreen() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { devices, fetchDevices, deleteDevice, setDeviceValue } = useDeviceStore();
  const { addToast, showLoading, hideLoading } = useUIStore();
  
  const [device, setDevice] = useState(devices.find(d => d.deviceId === deviceId));
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRoom, setEditRoom] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!device) fetchDevices();
  }, [device, fetchDevices]);

  useEffect(() => {
    setDevice(devices.find(d => d.deviceId === deviceId));
  }, [devices, deviceId]);

  useEffect(() => {
    if (device) {
      setEditName(device.customName || device.baseName);
      setEditRoom(device.room);
    }
  }, [device]);

  if (!device) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
            <X className="w-8 h-8 text-slate-300" />
          </div>
          <p className="text-slate-400">Устройство не найдено</p>
          <button onClick={() => navigate('/devices')} className="text-indigo-500 mt-4 font-medium">
            Назад
          </button>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    showLoading();
    try {
      await deleteDevice(deviceId!);
      addToast('Устройство удалено', 'success');
      navigate('/devices');
    } catch {
      addToast('Ошибка удаления', 'error');
    } finally {
      hideLoading();
    }
  };

  const handleParameterChange = async (parameter: Parameter, value: boolean | number | string) => {
    showLoading();
    try {
      const updatedParam: Parameter = {
        ...parameter,
        boolValue: typeof value === 'boolean' ? value : parameter.boolValue,
        numericValue: typeof value === 'number' ? value : parameter.numericValue,
        stringValue: typeof value === 'string' ? value : parameter.stringValue,
      };
      
      await setDeviceValue(deviceId!, updatedParam);
      
      setDevice(prev => prev ? {
        ...prev,
        parameters: prev.parameters.map(p => 
          p.name === parameter.name ? { ...p, ...updatedParam } : p
        )
      } : prev);
      
      addToast('Обновлено', 'success');
    } catch {
      addToast('Ошибка', 'error');
    } finally {
      hideLoading();
    }
  };

  const batteryParam = device.parameters.find(p => p.name === 'battery');
  const hasStats = device.parameters.some(p => 
    p.name.includes('temperature') || 
    p.name.includes('humidity') || 
    p.name.includes('power_consumption') ||
    p.name.includes('brightness')
  );

  return (
    <div className="min-h-screen pb-8">
      <motion.div 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="glass sticky top-0 z-20"
      >
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/devices')}
              className="w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </motion.button>
            
            <div className="flex items-center gap-2">
              {hasStats && (
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => navigate(`/statistics/${deviceId}`)}
                  className="w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center"
                >
                  <BarChart2 className="w-5 h-5 text-slate-600" />
                </motion.button>
              )}
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsEditing(!isEditing)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${isEditing ? 'gradient-primary' : 'bg-white/60'}`}
              >
                {isEditing 
                  ? <Check className="w-5 h-5 text-white" />
                  : <Edit2 className="w-5 h-5 text-slate-600" />
                }
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowDeleteConfirm(true)}
                className="w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center"
              >
                <Trash2 className="w-5 h-5 text-rose-500" />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-5">
            <div className={`
              w-20 h-20 rounded-2xl flex items-center justify-center
              ${device.isOnline ? 'gradient-primary shadow-lg shadow-indigo-500/30' : 'bg-slate-100'}
            `}>
              <DeviceIcon type={device.type} size="lg" className={device.isOnline ? 'text-white' : 'text-slate-300'} />
            </div>
            
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="text-xl font-bold text-slate-800 bg-white/80 rounded-xl px-4 py-2 w-full border border-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  autoFocus
                />
              ) : (
                <h1 className="text-xl font-bold text-slate-800">{device.customName || device.baseName}</h1>
              )}
              
              {isEditing ? (
                <input
                  type="text"
                  value={editRoom}
                  onChange={(e) => setEditRoom(e.target.value)}
                  className="text-slate-400 bg-white/80 rounded-xl px-4 py-2 mt-2 w-full border border-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              ) : (
                <p className="text-slate-400 mt-1">{device.room}</p>
              )}
              
              <div className="flex items-center gap-3 mt-3">
                <span className={`
                  inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
                  ${device.isOnline ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}
                `}>
                  <span className={`w-1.5 h-1.5 rounded-full ${device.isOnline ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  {device.isOnline ? 'Онлайн' : 'Оффлайн'}
                </span>
                {batteryParam && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                    <Battery className="w-3.5 h-3.5" />
                    {batteryParam.numericValue}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6"
        >
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Управление</h2>
          
          <div className="space-y-3">
            {device.parameters
              .filter(p => p.name !== 'battery' && !p.name.includes('moving'))
              .map((param, index) => (
                <motion.div
                  key={param.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.15 + index * 0.05 }}
                >
                  <ParameterControl
                    parameter={param}
                    onChange={(value) => handleParameterChange(param, value)}
                    disabled={!device.isOnline}
                  />
                </motion.div>
              ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6"
        >
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Информация</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between py-2">
              <span className="text-slate-400 text-sm">ID</span>
              <span className="text-slate-600 text-sm font-mono">{device.deviceId}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-400 text-sm">Тип</span>
              <span className="text-slate-600 text-sm">{device.rusType || device.type}</span>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/30 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-6 w-full max-w-sm"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-slate-800 mb-2">Удалить устройство?</h3>
              <p className="text-slate-400 text-sm mb-6">Это действие нельзя отменить</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-3 rounded-xl gradient-danger text-white font-medium"
                >
                  Удалить
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
